import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { DownloadIcon } from '@dynatrace/strato-icons';

import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { useDownload } from '../../hooks/useDownload.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';

export const DownloadDataButton = () => {
  const downloadDataButtonRef = useRef<HTMLButtonElement>(null);
  const tabIndex = useA11yToolbarTabIndex('download');
  const intl = useIntl();
  const { downloadDataCallback } = useDownload();
  const { seriesWithDatapoints, datapointMetadataMap, seriesToMetadata } =
    useXYChartData();

  const triggerDownload = () => {
    buildAndDownloadCsv(
      seriesWithDatapoints,
      seriesToMetadata,
      datapointMetadataMap,
      downloadDataCallback,
    );
  };

  const handleDownloadDataKeyboard = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      triggerDownload();
    }
  };

  const handleDownloadDataToolbar = () => {
    triggerDownload();
  };

  return (
    <ChartToolbarRenderer.Control
      ref={downloadDataButtonRef}
      prefixIcon={<DownloadIcon />}
      tooltipText={intl.formatMessage(XYCHART_MESSAGES.downloadAsCsv)}
      onKeyDown={handleDownloadDataKeyboard}
      onClick={handleDownloadDataToolbar}
      tabIndex={tabIndex}
      divider="left"
      data-dt-name="ToolbarDownloadData"
    />
  );
};

DownloadDataButton['displayName'] = 'DownloadDataButton';
